import { useCallback, useEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { routes } from 'types/routes'
import styles from './App.module.scss'
import { UserTypes } from 'store/auth/types'
import { useStoreDispatch, useStoreSelector } from 'hooks/store.ts'
import { useGetLanguage } from 'hooks/use-get-language'
import PageLoading from 'components/page-loading/PageLoading.tsx'
import EnergyTransitionDocumentContextProvider from 'store/react-context/energy-transition-document/EnergyTransitionDocumentContextProvider.tsx'
import { logout } from 'utils/app.ts'
import { setUserType } from 'utils/userType.ts'
import { fetchContactData, getUserTypeLoginPage } from 'utils/user.ts'
import AppNavigation from 'layouts/components/navigation/AppNavigation.tsx'
import classNames from 'classnames'
import useGetRouteLayout from 'hooks/useGetRouteLayout.ts'
import FeedbackWidget from 'components/feedback-widget/FeedbackWidget.tsx'
import useOpenFeedbackWidget from 'hooks/useOpenFeedbackWidget.ts'
import { WidgetSlug } from 'types/feedbackWidget.ts'
import { isAuthenticated } from 'utils/localStorageService.ts'
import { GetContactSuccessPayload } from 'store/contact/thunks/types.ts'
import { deleteRedirect, saveUrlSearchParam, selectUrlSearchParams, setShowSalesSurvey } from 'store/app/slice.ts'
import { setLogUser } from 'utils/logging.ts'
import { UrlSearchParamsKeys } from 'store/app/types.ts'
import { Layouts } from 'layouts/types.ts'
import { useGetActiveReferralActionQuery } from 'store/queries/cms-api'
import { getEmailByContactId } from 'store/contact/thunks'
import { selectContactByEmail } from 'store/contact/slice'
import FeedbackWidgetSalesSurvey from 'components/surveys/sales-survey/FeedbackWidgetSalesSurvey.tsx'
import { selectLastActivePushBillingContract } from 'store/contact/selectors.ts'
import { useGetMyEventsQuery } from 'store/queries/bolt-api/contacts'
import { EventLogType } from 'types/eventLog.ts'
import { checkCanTriggerSalesSurvey } from 'components/surveys/sales-survey/utils.ts'
import useNetworkState from 'hooks/useNetworkState.ts'
import { Flag } from '@boltenergy-be/design-system'
import { useTranslation } from 'react-i18next'

const App = () => {
  // Redux store
  const { redirect } = useStoreSelector((store) => store.app)
  const { error, contact, myContact } = useStoreSelector((store) => store.contact)
  const { userType } = useStoreSelector((store) => store.auth)
  const contactByEmail = useStoreSelector(selectContactByEmail)
  const contactId = useStoreSelector(selectUrlSearchParams)[UrlSearchParamsKeys.CONTACT_ID] || ''
  const dispatch = useStoreDispatch()
  const lastActivePushBillingContract = useStoreSelector(selectLastActivePushBillingContract)

  // i18n
  const { t } = useTranslation()
  const language = useGetLanguage()

  // Referral action
  const { data: activeAction } = useGetActiveReferralActionQuery({ language }, { skip: true }) // Skipped because there is no active referral action

  // Feedback widget
  const [hideFixedFeedbackWidget, setHideFixedFeedbackWidget] = useState<boolean>(false)
  const showFixedFeedbackWidget = !hideFixedFeedbackWidget && !error && userType === UserTypes.CUSTOMER
  const { feedbackWidgetData, openFeedbackWidget } = useOpenFeedbackWidget({
    slug: WidgetSlug.BETA_NEW_PORTAL,
    skip: !showFixedFeedbackWidget
  })

  // Events
  const { data: eventData, isLoading: isLoadingEvents } = useGetMyEventsQuery([EventLogType.SURVEY_CLOSED, EventLogType.SURVEY_RESPONDED], {
    skip: !contact?.email || !lastActivePushBillingContract
  })

  // React Router
  const { pathname } = useLocation()
  const navigate = useNavigate()

  // Network
  const { isOnline } = useNetworkState()

  // Auth constants
  const authenticatedUserTypes = isAuthenticated()

  // Local State
  const [isLoading, setIsLoading] = useState(true)

  // Constants
  const { layout } = useGetRouteLayout()
  const isEditingLayout = layout === Layouts.EDIT_LAYOUT || routes.USER_LOGIN_DETAILS_EDIT === pathname
  const hideNavigation =
    [routes.ACCOUNT_SELECTION, routes.CUSTOMER_EMAIL, routes.EVENT_LOG, routes.CONTRACTS].some((route) => pathname.includes(route)) ||
    isEditingLayout
  const isContractsFlow = pathname.includes(routes.CONTRACTS)
  const hideContent = isLoading || (userType !== UserTypes.SUPER_USER && error)
  const hasFixedFeedbackWidget = showFixedFeedbackWidget && feedbackWidgetData

  /**
   * Redirect to the sales portal or logout if the user is not a sales user
   */
  useEffect(() => {
    if (!authenticatedUserTypes[UserTypes.CUSTOMER] && !authenticatedUserTypes[UserTypes.SUPER_USER]) {
      logout(() => void navigate(getUserTypeLoginPage(userType)))
    }
  }, []) // oxlint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!userType && (authenticatedUserTypes[UserTypes.CUSTOMER] || authenticatedUserTypes[UserTypes.SUPER_USER])) {
      setUserType(authenticatedUserTypes[UserTypes.SUPER_USER] ? UserTypes.SUPER_USER : UserTypes.CUSTOMER)
    }
  }, [authenticatedUserTypes, userType])

  // Allow sales survey in the redux store based of the last active push billing contract and event data
  useEffect(() => {
    const salesSurveyRes = checkCanTriggerSalesSurvey({
      lastActivePushBillingContract,
      loading: isLoadingEvents,
      eventData: eventData || []
    })

    if (salesSurveyRes.show === 'intro') {
      setHideFixedFeedbackWidget(true)
    }

    if (salesSurveyRes.isEligible) {
      dispatch(setShowSalesSurvey(salesSurveyRes))
    }
  }, [lastActivePushBillingContract, eventData, isLoadingEvents, dispatch])

  /**
   * Handles the logout with correct redirect
   */
  const handleLogout = () =>
    logout(() => {
      setUserType(null)
      void navigate(getUserTypeLoginPage(userType))
    })

  /**
   * Navigate based on contact data
   * @param {GetContactSuccessPayload | undefined} getContactData
   */
  const navigateBasedOnContactData = useCallback(
    (getContactData: GetContactSuccessPayload | undefined) => {
      // Add user data to the logging context
      const contact = getContactData?.normalizedStoreData?.contact
      if (contact) {
        const { email, sfId } = contact
        setLogUser({ id: sfId, email })
      }

      if (
        getContactData?.selected &&
        getContactData.normalizedStoreData.accounts &&
        getContactData.selected.account in getContactData.normalizedStoreData.accounts
      ) {
        // Redirect if it is present in AppStore
        if (redirect) {
          void navigate(redirect)
          // Clear redirect from AppStore
          dispatch(deleteRedirect())
        } else {
          if (pathname === routes.ACCOUNT_SELECTION) {
            void navigate(routes.OVERVIEW)
          }
        }
      } else {
        // Multiple active contracts: redirect to choose address page
        void navigate(routes.ACCOUNT_SELECTION)
      }

      setIsLoading(false)
    },
    [dispatch, navigate, redirect]
  )

  /**
   * Initiate fetch contact data & navigate to correct page
   */
  const initiateContactData = useCallback(async () => {
    const contactData = await fetchContactData(UserTypes.CUSTOMER)

    if (contactData) {
      navigateBasedOnContactData(contactData)
    }

    setIsLoading(false)
  }, [navigateBasedOnContactData])

  // If the user is a superuser and there is a contactId in the URL we will try to get the contact data and navigate accordingly
  useEffect(() => {
    let ignore = false

    if (userType !== UserTypes.SUPER_USER || !contactId) {
      setIsLoading(false)
      return
    }

    const getData = async () => {
      try {
        // Fetch email by contactId from URL
        const response = await dispatch(getEmailByContactId({ contactId })).unwrap()

        // Clear contactId from URL search params
        dispatch(saveUrlSearchParam({ key: UrlSearchParamsKeys.CONTACT_ID, value: '' }))

        // Fetch contact data for the super user
        const email = response.email
        const contactData = await fetchContactData(UserTypes.SUPER_USER, email)

        if (!contactData) {
          throw new Error('No contact data found')
        }

        void navigate(routes.ACCOUNT_SELECTION)
      } catch (error) {
        void navigate(routes.CUSTOMER_EMAIL)
      } finally {
        setIsLoading(false)
      }
    }

    if (!ignore) {
      void getData()
    }

    return () => {
      ignore = true
    }
  }, [dispatch, navigate, contactId, userType])

  useEffect(() => {
    if (userType === UserTypes.SUPER_USER && !contactByEmail.loading && !contactByEmail.success) {
      void navigate(routes.CUSTOMER_EMAIL)
    }
  }, [contactByEmail.loading, contactByEmail.success, navigate, userType])

  // Fetch contact data for UserTypes.CUSTOMER
  useEffect(() => {
    if (!userType || userType === UserTypes.SALES || userType === UserTypes.SUPER_USER) return

    let ignore = false

    if (!ignore) {
      // Fetch 'normal' user data
      void initiateContactData()
    }

    return () => {
      ignore = true
    }
  }, [initiateContactData, userType])

  // Toggle overflow
  useEffect(() => {
    if (pathname === routes.OVERVIEW) {
      document.body.classList.add('overview-page')
      document.body.scrollTo(0, 0)
    } else {
      document.body.classList.remove('overview-page')
    }
  }, [pathname])

  return isContractsFlow ? (
    // Contracts layout is inside the pages
    <EnergyTransitionDocumentContextProvider>
      {hideContent ? <PageLoading {...{ isLoading }} /> : <Outlet />}
    </EnergyTransitionDocumentContextProvider>
  ) : (
    <div
      className={classNames(styles['app-layout'], {
        [styles['has-navigation']]: !hideNavigation
      })}
    >
      {!hideNavigation && <AppNavigation handleLogout={handleLogout} withAction={!!activeAction} />}

      <main className={classNames(styles.main, { [styles.wide]: isEditingLayout })}>
        {!isOnline && !error && (
          <Flag type="attention" title={t(($) => $.network.warning.title)} className={styles.flag} size="small">
            <p>{t(($) => $.network.warning.description)}</p>
          </Flag>
        )}

        {hideContent || myContact.loading ? <PageLoading isLoading={isLoading || myContact.loading} /> : <Outlet />}
      </main>

      {hasFixedFeedbackWidget && (
        <FeedbackWidget
          className={styles.feedback}
          data={{ page: pathname }}
          id={feedbackWidgetData._id}
          openFeedbackWidget={openFeedbackWidget}
          showTrigger={showFixedFeedbackWidget}
          isGeneralFeedback={showFixedFeedbackWidget}
        />
      )}

      {/* Sales Survey Widget*/}
      <FeedbackWidgetSalesSurvey />
    </div>
  )
}

export default App
